import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { fetchLeads, updateLeadStatus } from "../lib/api";

const STATUS = ["new", "contacted", "qualified", "won", "lost"];

export default function AdminDashboard() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();
  const token = localStorage.getItem("cc_admin_token");

  const load = async () => {
    if (!token) { nav("/admin"); return; }
    setLoading(true);
    try {
      const data = await fetchLeads(token);
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("cc_admin_token");
        nav("/admin");
      } else {
        toast.error("Could not load leads.");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onStatusChange = async (id, status) => {
    try {
      await updateLeadStatus(token, id, status);
      setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status } : l));
      toast.success("Status updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => { localStorage.removeItem("cc_admin_token"); nav("/admin"); };

  const counts = STATUS.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <div className="container-x section-pad" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="accent-bar mb-2" />
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-navy">Lead Management</h1>
          <p className="text-sm text-muted-foreground">{leads.length} total leads</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="border border-border bg-white px-4 py-2 text-sm rounded-sm flex items-center gap-2 hover:border-navy" data-testid="admin-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button onClick={logout} className="bg-navy text-white px-4 py-2 text-sm rounded-sm flex items-center gap-2 hover:bg-navy/90" data-testid="admin-logout">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        {STATUS.map((s) => (
          <div key={s} className="border border-border bg-white p-4 rounded-sm" data-testid={`stat-${s}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{s}</div>
            <div className="stat-num text-3xl text-navy mt-1">{counts[s]}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading...</div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-sm text-muted-foreground">No leads yet. Submissions from the website will appear here.</div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-white">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-secondary text-navy">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Loan Type</th>
                <th className="text-left px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">City</th>
                <th className="text-left px-4 py-3">Message</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-border" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3 font-medium text-navy">{l.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-xs hover:text-brandred"><Mail size={12} />{l.email}</a>
                      <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-xs hover:text-brandred"><Phone size={12} />{l.phone}</a>
                    </div>
                  </td>
                  <td className="px-4 py-3">{l.loan_type}</td>
                  <td className="px-4 py-3">{l.amount || "—"}</td>
                  <td className="px-4 py-3">{l.city || "—"}</td>
                  <td className="px-4 py-3 max-w-xs"><div className="truncate" title={l.message}>{l.message || "—"}</div></td>
                  <td className="px-4 py-3">
                    <select value={l.status} onChange={(e) => onStatusChange(l.id, e.target.value)} className="border border-input bg-white px-2 py-1 text-xs rounded-sm" data-testid={`status-select-${l.id}`}>
                      {STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
